import { useCallback, useEffect, useId, useMemo, useState, type SetStateAction } from 'react'
import {
  ACOES_AVALIACAO,
  ESTADO_VITIMA_VAZIO,
  ROTULOS_REGIAO,
  VITIMAS_CADAS_TRADAS,
  VITIMAS_POR_ID,
  alternarCronometro,
  alternarModoExibicao,
  avancarEstadoVitima,
  definirFocoVitima,
  estagiosDaVitima,
  itensNuncaPesquisados,
  momentoDeteccaoDeterioracao,
  registrarInterpretacao,
  registrarIntervencao,
  registrarPassagemDeCaso,
  regioesPreviveisXInvestigadas,
  revelarAcao,
  situacaoDaVerificacao,
  achadoAtualDaVitima,
  type CinematicaFichaVitima,
  type EtapaAvaliacao,
  type FichaVitimaAtendimento,
  type IdAcaoAvaliacao,
  type InterpretacaoLetra,
  type LetraXabcde,
  type SessaoAtendimentoTabletop,
} from '../../dominio/tabletop/atendimento'
import { condutasDaFicha, etapaDoProblema } from './condutasDaFicha'
import { avancarRelogioOficina, configurarPiora, criarAtendimentoOficina, motivoDaPiora, prepararProgramacao, programacaoDaVitima, validarRespostaRespiratoria } from './evolucaoProgramada'
import { FichaClinicaVitima } from '../vitima/FichaClinicaVitima'
import './OficinaVitimas.css'

type Aba = 'ficha' | 'passagem'

const ABAS: ReadonlyArray<{ id: Aba; rotulo: string }> = [
  { id: 'ficha', rotulo: 'Ficha clínica' },
  { id: 'passagem', rotulo: 'Passagem' },
]

const ROTULOS_CONDUTA = {
  compressao_direta: 'Compressão direta', curativo_compressivo: 'Curativo compressivo', torniquete: 'Torniquete',
  colar_cervical: 'Colar cervical', oxigenioterapia: 'Oxigenoterapia', manta_termica: 'Manta térmica', imobilizacao_membro: 'Imobilização',
} as const

const LETRAS: readonly string[] = ['X', 'A', 'B', 'C', 'D', 'E']

const ROTULOS_INTERPRETACAO: ReadonlyArray<{ id: InterpretacaoLetra; rotulo: string }> = [
  { id: 'sem_alteracao', rotulo: 'sem alteração' },
  { id: 'alterado', rotulo: 'alterado' },
  { id: 'ameaca_vida', rotulo: 'ameaça à vida' },
]

function formatarTempo(segundos: number): string {
  const min = Math.floor(segundos / 60)
  const seg = segundos % 60
  return `${min.toString().padStart(2, '0')}:${seg.toString().padStart(2, '0')}`
}

/**
 * Estado do atendimento fica no App para a Oficina não reiniciar ao alternar
 * com a Retirada angulada. É a mesma sessão do atendimento do Tabletop, sem
 * cena: cada vítima do catálogo é identificada pelo id da própria ficha.
 */
export function useEstadoOficinaVitimas() {
  const [atendimento, setAtendimento] = useState(() => criarAtendimentoOficina(VITIMAS_CADAS_TRADAS[0]!.id))
  const sessao = atendimento.sessao
  const setSessao = useCallback((acao: SetStateAction<SessaoAtendimentoTabletop>) => {
    setAtendimento(atual => prepararProgramacao({ ...atual, sessao: typeof acao === 'function' ? acao(atual.sessao) : acao }))
  }, [])
  const [aba, setAba] = useState<Aba>('ficha')
  const [gabaritoVisivel, setGabaritoVisivel] = useState(false)

  useEffect(() => {
    if (!sessao.cronometroRodando) return
    const intervalo = setInterval(() => {
      setAtendimento(avancarRelogioOficina)
    }, 1000)
    return () => clearInterval(intervalo)
  }, [sessao.cronometroRodando])

  const reiniciar = () => {
    setAtendimento(atual => criarAtendimentoOficina(atual.sessao.focoVitimaId ?? VITIMAS_CADAS_TRADAS[0]!.id))
    setAba('ficha')
    setGabaritoVisivel(false)
  }

  return { atendimento, setAtendimento, sessao, setSessao, aba, setAba, gabaritoVisivel, setGabaritoVisivel, reiniciar }
}

type Estado = ReturnType<typeof useEstadoOficinaVitimas>

const caixa: React.CSSProperties = {
  padding: 16, border: '1px solid var(--color-border-control)',
  borderRadius: 12, background: 'var(--color-background-subtle)', marginBottom: 16,
}
const botoes: React.CSSProperties = { display: 'flex', flexWrap: 'wrap', gap: 8, margin: '12px 0', alignItems: 'center' }
const classeBotao = (ativo: boolean) =>
  ativo ? 'btn-acao-cad btn-acao-cad--destaque' : 'btn-acao-cad btn-acao-cad--secundario'

interface Props {
  readonly estado: Estado
  /**
   * Leva a aba Cinemática do app para o mecanismo desta ficha (colisão,
   * assento, cinto). A cena 3D não vive na Oficina — ela reaproveita a que
   * já existe, então quem soube trocar de aba (App.tsx) decide o resto.
   */
  readonly aoVerCinematica?: (cinematica: CinematicaFichaVitima) => void
  readonly aoVoltarRetirada?: () => void
}

export function OficinaVitimas({ estado, aoVerCinematica, aoVoltarRetirada }: Props) {
  const idBase = useId()
  const { sessao, setSessao } = estado
  const ficha = VITIMAS_POR_ID[sessao.focoVitimaId ?? ''] ?? VITIMAS_CADAS_TRADAS[0]!
  const estadoVitima = sessao.estadosVitimas[ficha.id] ?? ESTADO_VITIMA_VAZIO
  const modoInstrutor = sessao.modoExibicao === 'instrutor'
  const situacao = useMemo(() => situacaoDaVerificacao(ficha, estadoVitima), [ficha, estadoVitima])
  const estagios = estagiosDaVitima(ficha)
  const leiturasAtuais = useMemo(() => new Map(ACOES_AVALIACAO.map(acao =>
    [acao.id, achadoAtualDaVitima(ficha, acao.id, estadoVitima)],
  )), [ficha, estadoVitima])
  const condutas = useMemo(() => condutasDaFicha(ficha), [ficha])
  const programacao = programacaoDaVitima(estado.atendimento, ficha)
  const motivoPiora = motivoDaPiora(estado.atendimento, ficha)
  const ultimoEventoPiora = sessao.historicoEventos.find(e => e.objetoVitimaId === ficha.id && e.descricao.startsWith('Piora programada:'))
  const ultimoEstagio = estagios.at(-1)?.id === estadoVitima.estadoEvolucao
  const regrasDaEtapa = (etapa: EtapaAvaliacao) => (ficha.regrasEfeito ?? []).filter(regra => {
    const problema = ficha.problemas?.find(p => p.id === regra.idProblema)
    return problema && etapaDoProblema(problema) === etapa
  })

  const revelar = (idAcao: IdAcaoAvaliacao) =>
    setSessao((atual) => revelarAcao(atual, ficha.id, idAcao, ficha))

  /** a equipe interpreta a letra depois de examinar ao menos um item dela */
  const interpretacaoDaLetra = (etapa: EtapaAvaliacao) => {
    if (!LETRAS.includes(etapa)) return null
    const letra = etapa as LetraXabcde
    const examinou = ACOES_AVALIACAO.some((a) => a.etapa === etapa && estadoVitima.acoesReveladas.includes(a.id))
    if (!examinou) return null
    return (
      <span role="group" aria-label={`Interpretação da equipe ${letra}`}>
        {ROTULOS_INTERPRETACAO.map(({ id, rotulo }) => (
          <button key={id} type="button" aria-pressed={estadoVitima.interpretacoes[letra] === id}
            onClick={() => setSessao((atual) => registrarInterpretacao(atual, ficha.id, letra, id))}>
            {rotulo}
          </button>
        ))}
      </span>
    )
  }

  return (
    <section className="oficina-vitimas" aria-label="Oficina de Vítimas">
      <div className="oficina-vitimas__conteudo">
        <header className="oficina-vitimas__cabecalho">
          <h2>Oficina de Vítimas</h2>
          {aoVoltarRetirada && <button type="button" className="btn-acao-cad btn-acao-cad--secundario"
            onClick={aoVoltarRetirada} aria-label="Voltar à retirada angulada">Retirada angulada</button>}
        </header>
        <label className="oficina-vitimas__vitima">
          Vítima
          <select aria-label="Vítima" value={ficha.id}
            onChange={(e) => {
              setSessao((atual) => definirFocoVitima(atual, e.target.value))
              estado.setAba('ficha')
              estado.setGabaritoVisivel(false)
            }}>
            {VITIMAS_CADAS_TRADAS.map((v) => <option key={v.id} value={v.id}>{v.titulo}</option>)}
          </select>
        </label>

        <div className="oficina-vitimas__relogio" role="group" aria-label="Relógio do atendimento">
          <span aria-label="Tempo do atendimento">{formatarTempo(sessao.tempoSimuladoSegundos)}</span>
          <button type="button" className={classeBotao(sessao.cronometroRodando)}
            onClick={() => setSessao((atual) => alternarCronometro(atual))}>
            {sessao.cronometroRodando ? 'Pausar relógio' : 'Iniciar relógio'}
          </button>
        </div>

        <details className="oficina-vitimas__opcoes">
          <summary>Opções do atendimento{modoInstrutor ? ' · ficha do instrutor ativa' : ''}</summary>
          <div className="oficina-vitimas__acoes">
            <button type="button" className={classeBotao(modoInstrutor)} aria-pressed={modoInstrutor}
              onClick={() => setSessao((atual) => alternarModoExibicao(atual))}>Ficha do instrutor</button>
            <button type="button" className="btn-acao-cad btn-acao-cad--secundario" onClick={estado.reiniciar}>
              Reiniciar atendimento
            </button>
            {ficha.cinematica && aoVerCinematica && (
              <button type="button" className="btn-acao-cad btn-acao-cad--secundario"
                onClick={() => aoVerCinematica(ficha.cinematica!)}
                title="Mostra o impacto para a turma — mecanismo, não a ficha clínica">Ver cinemática desta vítima</button>
            )}
          </div>
          <fieldset className="oficina-vitimas__programacao">
            <legend>Pioras programadas</legend>
            <label><input type="checkbox" checked={programacao.configuracao.ativa} disabled={estagios.length < 2}
              onChange={e => estado.setAtendimento(atual => configurarPiora(atual, ficha, { ...programacao.configuracao, ativa: e.target.checked }))} />
              Ativar evolução pelo relógio</label>
            {estagios.length < 2 ? <p>Esta ficha não possui estágio de piora cadastrado.</p> : <>
              <label>Minutos entre pioras
                <input type="number" min={1} max={15} step={1} value={programacao.configuracao.intervaloSegundos / 60}
                  onChange={e => estado.setAtendimento(atual => configurarPiora(atual, ficha, { ...programacao.configuracao, intervaloSegundos: e.target.valueAsNumber * 60 }))} />
              </label>
              <p>Tempos didáticos do exercício. Alterar o intervalo inicia uma nova contagem para a próxima etapa.</p>
            </>}
          </fieldset>
        </details>

        {programacao.configuracao.ativa && estagios.length > 1 && <p className="oficina-vitimas__evolucao">
          {ultimoEstagio ? 'Último estágio do cenário.' : !motivoPiora ? 'Próxima piora suspensa pela resposta à conduta.'
            : programacao.proximoEm === null ? `Piora prevista após ${formatarTempo(programacao.configuracao.intervaloSegundos)} de relógio.`
              : `Próxima piora aos ${formatarTempo(programacao.proximoEm)} do relógio, se a condição persistir.`}
        </p>}
        {ultimoEventoPiora && <div className="oficina-vitimas__aviso" role="status">
          <strong>{formatarTempo(ultimoEventoPiora.tempoSimuladoSegundos)} — {ultimoEventoPiora.descricao}</strong>
          <p>{ultimoEventoPiora.detalhes} Confira os achados atualizados e reavalie a vítima.</p>
        </div>}

        <div className="oficina-vitimas__abas" role="tablist" aria-label="Atendimento da vítima">
          {ABAS.map(({ id, rotulo }) => (
            <button key={id} type="button" role="tab" aria-selected={estado.aba === id}
              id={`${idBase}-aba-${id}`} aria-controls={`${idBase}-painel`}
              className={classeBotao(estado.aba === id)} onClick={() => estado.setAba(id)}>{rotulo}</button>
          ))}
        </div>
        <div id={`${idBase}-painel`} role="tabpanel" aria-labelledby={`${idBase}-aba-${estado.aba}`}>
          {estado.aba === 'ficha' && (
            <section className="oficina-vitimas__ficha">
              <FichaClinicaVitima
                key={ficha.id}
                ficha={ficha}
                modo={modoInstrutor ? 'instrutor' : 'turma'}
                estagioAtual={estadoVitima.estadoEvolucao}
                verificados={situacao.verificados}
                desatualizados={situacao.desatualizados}
                leituras={leiturasAtuais}
                travarVerificados
                leiturasSempreVisiveis
                mostrarProgresso={false}
                aoAlternarVerificado={(idAcao, marcado) => { if (marcado) revelar(idAcao) }}
                aoMudarEstagio={(idEstagio) => setSessao((atual) => avancarEstadoVitima(
                  atual, ficha.id, idEstagio, estagios.find((e) => e.id === idEstagio)?.rotulo,
                ))}
                acoesDoGrupo={interpretacaoDaLetra}
                conteudoDoGrupo={(etapa) => {
                  const textos = condutas.get(etapa)
                  const regras = regrasDaEtapa(etapa)
                  const respiracao = etapa === 'B' && ficha.id === 'vitima3'
                  if (!textos?.length && !respiracao) return null
                  return (
                    <div className="oficina-vitimas__condutas" aria-label={`Condutas do cenário — ${etapa}`}>
                      <strong>Condutas neste cenário</strong>
                      {textos?.map(texto => <p key={texto}>{texto}</p>)}
                      {regras.map(regra => {
                        const regiao = regra.regioes?.[0] ?? ficha.problemas!.find(p => p.id === regra.idProblema)!.regiao
                        if (regra.tipoIntervencao === 'torniquete' && !regiao.startsWith('membro')) return null
                        const rotulo = `${ROTULOS_CONDUTA[regra.tipoIntervencao]} — ${ROTULOS_REGIAO[regiao].toLowerCase()}`
                        const realizada = estadoVitima.intervencoesRealizadas.some(i => i.tipo === regra.tipoIntervencao && i.regiao === regiao)
                        return <label className="oficina-vitimas__conduta" key={regra.id}>
                          <input type="checkbox" checked={realizada} disabled={realizada} aria-label={`Conduta realizada: ${rotulo}`}
                            onChange={() => setSessao(atual => registrarIntervencao(atual, ficha.id, regra.tipoIntervencao, rotulo, {}, undefined, { ficha, regiao }))} />
                          <span>{rotulo} · {realizada ? 'conduta registrada' : 'marcar após realizar'}</span>
                        </label>
                      })}
                      {respiracao && <label className="oficina-vitimas__conduta">
                        <input type="checkbox" checked={programacao.respostaRespiratoriaValidada}
                          aria-label="Resposta à conduta respiratória validada no cenário"
                          onChange={e => estado.setAtendimento(atual => validarRespostaRespiratoria(atual, ficha, e.target.checked))} />
                        <span>Resposta à conduta respiratória validada no cenário pelo instrutor. Suspende a próxima piora; mantém os achados atuais.</span>
                      </label>}
                      {regras.length > 0 && <p className="oficina-vitimas__nota-conduta">Marque a conduta após sua realização no exercício. O check do exame registra somente a avaliação.</p>}
                    </div>
                  )
                }}
              />
            </section>
          )}

          {estado.aba === 'passagem' && <Passagem estado={estado} ficha={ficha} />}
        </div>
      </div>
    </section>
  )
}

/** A equipe passa o caso; depois o professor abre as considerações da ficha para a discussão. */
function Passagem({ estado, ficha }: { estado: Estado; ficha: FichaVitimaAtendimento }) {
  const { sessao, setSessao } = estado
  const [texto, setTexto] = useState('')
  const estadoVitima = sessao.estadosVitimas[ficha.id] ?? ESTADO_VITIMA_VAZIO
  const passagens = sessao.historicoEventos
    .filter((e) => e.objetoVitimaId === ficha.id && e.descricao === 'Passagem de caso da equipe')
    .slice().reverse()
  const gabarito = ficha.gabaritoInstrutor
  const nuncaPesquisados = itensNuncaPesquisados(ficha, estadoVitima)
  const deteccao = momentoDeteccaoDeterioracao(sessao, ficha.id)
  const licoesCinematica = regioesPreviveisXInvestigadas(ficha, estadoVitima)

  return (
    <section style={caixa}>
      <h3>Passagem de caso</h3>
      <label>
        O que a equipe relatou
        <textarea style={{ display: 'block', width: '100%', boxSizing: 'border-box', minHeight: 96 }}
          value={texto} onChange={(e) => setTexto(e.target.value)}
          placeholder="Mecanismo, achados, o que foi feito, reavaliação e prioridade" />
      </label>
      <div style={botoes}>
        <button type="button" className="btn-acao-cad btn-acao-cad--primario" disabled={!texto.trim()}
          onClick={() => { setSessao((atual) => registrarPassagemDeCaso(atual, ficha.id, texto)); setTexto('') }}>
          Registrar passagem
        </button>
      </div>
      {passagens.map((p) => (
        <p key={p.id}><strong>{formatarTempo(p.tempoSimuladoSegundos)}</strong> — {p.detalhes}</p>
      ))}

      <button type="button" className="btn-acao-cad btn-acao-cad--destaque"
        onClick={() => estado.setGabaritoVisivel(!estado.gabaritoVisivel)}>
        {estado.gabaritoVisivel ? 'Ocultar considerações' : 'Revelar considerações do instrutor'}
      </button>
      {estado.gabaritoVisivel && (
        <div style={{ marginTop: 16 }}>
          {gabarito && (
            <>
              <p>Triagem sugerida: {gabarito.triagemSugerida} — {gabarito.justificativaTriagem}</p>
              <p>Prioridade de extração: {gabarito.prioridadeExtracao}</p>
              <ul>{gabarito.notasDebriefing.map((nota) => <li key={nota}>{nota}</li>)}</ul>
            </>
          )}
          {ficha.pendencias.length > 0 && (
            <>
              <strong>Pendências da ficha</strong>
              <ul>{ficha.pendencias.map((p) => <li key={p}>{p}</li>)}</ul>
            </>
          )}
          {nuncaPesquisados.length > 0 && (
            <>
              <strong>Nunca pesquisado</strong>
              <ul>{nuncaPesquisados.map((item) => <li key={item.idAcao}>{item.rotulo}: {item.achado.valor}</li>)}</ul>
            </>
          )}
          <p>
            Deterioração percebida: {deteccao !== null
              ? `em ${formatarTempo(deteccao)}`
              : sessao.historicoEventos.some((e) => e.objetoVitimaId === ficha.id && e.tipo === 'evolucao')
                ? 'ainda não percebida — ninguém reavaliou desde a evolução'
                : 'sem evolução disparada nesta sessão'}
          </p>
          {licoesCinematica.length > 0 && (
            <>
              <strong>Lesões previsíveis pela cinemática</strong>
              <ul>{licoesCinematica.map(({ regiao, investigada }) => (
                <li key={regiao}>{ROTULOS_REGIAO[regiao]}: {investigada ? 'investigada' : 'não investigada'}</li>
              ))}</ul>
            </>
          )}
          <p>As considerações são específicas desta ficha e permitem alternativas justificadas.</p>
        </div>
      )}
    </section>
  )
}
